// File: components/audio/GameAudioManager.ts
import * as Tone from 'tone';
import { Midi } from '@tonejs/midi';
import { SONG_REGISTRY } from './songs';
import { HuntrixSong, SongSection, SequencedTrack } from './songs/types';
import { SCALES } from './constants';
import { GameAudioContext, initializeAudioContext } from './instruments';
import { getKickPattern, getSnarePattern } from './patterns';
import { safeRamp } from './utils';

export type MusicMixMode = 'SEQUENCE' | 'MIDI' | 'MP3';

export interface LyricLine {
    text: string;
    singer?: 'RUMI' | 'MIRA' | 'ZOEY' | 'ALL';
}

export interface LyricContext {
    prev: LyricLine;
    current: LyricLine;
    next: LyricLine;
}

/**
 * --- TONE.JS RESEARCH & OPTIMIZATION NOTES (Jan 2026) ---
 * 
 * 1. RESOURCE LEAKS & GHOST SYNTHS:
 *    - Problem: React Strict Mode (Dev) double-invokes `useEffect`, creating duplicate Tone.js instances 
 *      if not disposed. Chrome's AudioContext hardware limit (~6 per tab) is easily reached.
 *    - Solution: ALWAYS return a cleanup function: `return () => synth.dispose();`.
 *    - Solution: Use a Singleton pattern (`isInitialized` check below) to reuse the specific `GameAudioContext`.
 * 
 * 2. CPU THROTTLING & FREEZING:
 *    - Problem: Direct calls to `triggerAttackRelease` (fire-and-forget) during rapid user interaction
 *      (e.g., spam-clicking) causes "Voice Buildup". Each overlapping note adds CPU load.
 *    - Fix: We implemented `playSFX()` with a specific 50ms throttling map (`lastSfxTime`). 
 *      This prevents "machine gun" execution.
 * 
 * 3. AUDIO-VISUAL SYNC:
 *    - Problem: `Tone.Transport` runs on the Audio Clock (high precision, worker thread), while React 
 *      updates on the Main Thread. Updating UI directly from Transport causes jitter/desync.
 *    - Fix: Use `Tone.Draw.schedule(() => { setUI(...) }, time)` to align audio events with the 
 *      nearest RequestAnimationFrame paint.
 * 
 * 4. MEMORY & GARBAGE COLLECTION:
 *    - Tone.js nodes (Filters, Delays) created in `instruments.ts` but not connected/returned will 
 *      leak memory on every reboot. We now verify all nodes are exposed in `GameAudioContext` for 
 *      disposal in `hardReboot()`.
 */
export class GameAudioManager {
    private isInitialized = false;
    private loadSessionId = 0;
    public isPlaying = false;
    public ctx: GameAudioContext | null = null;
    private midiParts: Tone.Part[] = [];
    private backingPlayer: Tone.Player | null = null;
    private pendingPlayer: Tone.Player | null = null; // Track players currently loading to prevent leaks
    private lastNoteTriggered: string = "None";
    private currentSongId: string | null = null;
    private currentMixMode: MusicMixMode = 'SEQUENCE'; // Track the last used mix mode
    private endEventId: number | null = null;
    private isRebooting = false;
    private scheduledEvents: number[] = [];
    private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
    private silentFrameCount = 0; // Tracks consecutive frames with no audio output

    public onLyricUpdate: ((data: LyricContext) => void) | null = null;
    public onSectionChange: ((type: string) => void) | null = null;
    public onError: ((msg: string) => void) | null = null;
    public onStatus: ((msg: string) => void) | null = null;
    public onSongEnd: (() => void) | null = null;
    private isInitializing = false;
    private isPlayingSong = false;
    private logCounter = 0;

    // SFX Throttling
    private lastSfxTime: Map<string, number> = new Map();

    public playSFX(instrumentKey: keyof GameAudioContext, note: string | string[], duration: string = "8n", velocity: number = 1) {
        if (!this.ctx || Tone.context.state !== 'running') return;

        const now = Date.now();
        const lastTime = this.lastSfxTime.get(instrumentKey as string) || 0;

        // Limit specific instrument triggers to 50ms
        if (now - lastTime < 50) return;

        try {
            const instrument = this.ctx[instrumentKey];
            if (instrument && (instrument as any).triggerAttackRelease) {
                // Tone.js Signature Mismatch Handling:
                // Synths: triggerAttackRelease(note, duration, time, velocity)
                // NoiseSynths: triggerAttackRelease(duration, time, velocity)

                const isNoise = instrument.name === 'NoiseSynth' || instrumentKey === 'noiseSynth' || instrumentKey === 'clap';

                if (isNoise) {
                    // For Noise, 'note' argument is actually duration. 'duration' argument is ignored/undefined for time.
                    (instrument as any).triggerAttackRelease(note, Tone.now(), velocity);
                } else {
                    (instrument as any).triggerAttackRelease(note, duration, Tone.now(), velocity);
                }
                this.lastSfxTime.set(instrumentKey as string, now);
            }
        } catch (e) { }
    }

    public async initialize() {
        if (this.isRebooting || this.isInitializing) return;
        this.isInitializing = true;
        let builtContext: Tone.Context | undefined;

        try {
            // Check if context is closed or needs to be created
            if (Tone.context.state === 'closed') {
                this.reportStatus("Context was closed. Rebuilding engine instance...");

                this.reportStatus("Context was closed. Rebuilding engine instance...");

                const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
                const rawCtx = new AudioContextClass();
                // FAST RESUME: Capture user gesture immediately
                try { rawCtx.resume(); } catch (e) { }

                builtContext = new Tone.Context(rawCtx);
                Tone.setContext(builtContext);

                // FORCE UPDATE: Internal gut swap for persistent global wrappers
                if ((Tone.context as any)._context) {
                    (Tone.context as any)._context = rawCtx;
                }

                // CRITICAL SYNC: Ensure Transport and Destination use the new context
                try {
                    (Tone.Transport as any).context = builtContext;
                    (Tone.Destination as any).context = builtContext;
                } catch (e) { }

                this.isInitialized = false;
                this.ctx = null;
            }

            // CRITICAL: Start context IMMEDIATELY while still in user gesture
            if (Tone.context.state === 'suspended') {
                await Tone.start();
                if (Tone.context.state === 'suspended') {
                    await (Tone.context.rawContext as AudioContext).resume();
                }
            }

            // Now initialize/reinitialize audio nodes if needed
            if (!this.isInitialized || !this.ctx) {
                this.ctx = await initializeAudioContext(builtContext);
                this.isInitialized = true;
                this.toggleMute(Tone.Destination.mute);
                this.reportStatus("Audio Engine Initialized");
            }
        } catch (e) {
            this.reportError(`Failed to init Audio: ${e}`);
        } finally {
            this.isInitializing = false;
        }
    }

    public async hardReboot() {
        if (this.isRebooting) {
            this.reportStatus("Reboot already in progress...");
            return;
        }
        this.isRebooting = true;
        this.reportStatus("!!! INITIATING HARD AUDIO REBOOT !!!");

        const cachedSongId = this.currentSongId;
        const cachedIsPlaying = this.isPlaying;
        const cachedMixMode = this.currentMixMode;

        // CRITICAL FIX: Create context SYNC at start to capture user gesture
        // If we wait for the timeout, the browser will block the new context
        this.reportStatus("Requesting new hardware handle (SYNC+RESUME)...");
        const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
        const rawCtx: AudioContext = new AudioContextClass();
        // RESUME IMMEDIATELY to burn the user gesture token
        try { rawCtx.resume(); } catch (e) { }

        try {
            // Step 1: Stop and Cleanup
            this.stopMusic();
            if (this.ctx) {
                Object.values(this.ctx).forEach((node: any) => {
                    if (node && typeof node.dispose === 'function') {
                        try { node.dispose(); } catch (e) { }
                    }
                });
                this.ctx = null;
            }
            this.isInitialized = false;

            // Step 1: Force stop and reset Transport to prevent timing desyncs
            Tone.Transport.stop();
            Tone.Transport.position = 0;
            Tone.Transport.cancel();

            // Step 2: Close the RAW AudioContext only (do NOT dispose Tone wrapper yet)
            // Disposing Tone's context wrapper before setContext can corrupt internal state
            const oldRawContext = Tone.context.rawContext as AudioContext;
            if (oldRawContext && oldRawContext.state !== 'closed') {
                this.reportStatus(`Closing old raw context (state: ${oldRawContext.state})...`);
                try {
                    await oldRawContext.close();
                } catch (e) {
                    this.reportStatus(`Context close warning: ${e}`);
                }
            }

            // Step 3: Hardware release cooldown (reduced to 500ms)
            // This is critical for the OS to release the hardware handle
            this.reportStatus("Waiting for hardware release...");
            await new Promise(resolve => setTimeout(resolve, 500));

            // Step 4: Use the PRE-CREATED hardware handle
            this.reportStatus("Swapping in new handle...");

            // SUPER AGGRESSIVE: Manually purge window context if possible
            if ((window as any).ToneContext) {
                try { (window as any).ToneContext.close(); } catch { }
            }

            // Step 5: Wrap in Tone and Set - WITH EXPLICIT GLOBAL SETTER
            const freshToneContext = new Tone.Context(rawCtx);
            Tone.setContext(freshToneContext);

            // FORCE UPDATE: Internal gut swap for persistent global wrappers
            // This fixes the "Tone=closed" issue when setContext fails to replace the global
            if ((Tone.context as any)._context) {
                (Tone.context as any)._context = rawCtx;
            }

            // CRITICAL SYNC: Manually ensure Transport and Destination use this context
            // Some Tone.js builds/environments don't propagate setContext correctly to these singletons
            try {
                (Tone.Transport as any).context = freshToneContext;
                (Tone.Destination as any).context = freshToneContext;

                // Also update internal context if it's a private property
                if ((Tone.Transport as any)._context) (Tone.Transport as any)._context = freshToneContext;
                if ((Tone.Destination as any)._context) (Tone.Destination as any)._context = freshToneContext;
            } catch (e) {
                this.reportStatus(`Singleton sync warning: ${e}`);
            }

            this.reportStatus(`Global context swapped. New state: ${Tone.context.state}`);
            this.reportStatus(`New handle Verify: raw=${rawCtx.state}, Tone=${Tone.context.state}`);

            // Step 6: Start the new context (must happen within user gesture)
            if (rawCtx.state === 'suspended') {
                this.reportStatus("Waking up hardware...");
                try {
                    await freshToneContext.resume();
                    await Tone.start(); // Also ensure Tone is started on this context
                } catch (e) {
                    this.reportStatus(`Hardware wakeup warning: ${e}`);
                }
            }

            if (rawCtx.state !== 'running') {
                this.reportError(`Hardware check FAILED. State: ${rawCtx.state}. The browser might be blocking audio.`);
            } else {
                this.reportStatus("Hardware verified: RUNNING");
            }

            // Step 7: Initialize fresh audio nodes with EXPLICIT context
            this.ctx = await initializeAudioContext(freshToneContext);
            this.isInitialized = true;
            this.toggleMute(Tone.Destination.mute);
            this.reportStatus("Audio engine nodes rebuilt (Explicitly bound).");

            // DIAGNOSTIC BEEP: Prove hardware is alive
            // This connects a raw oscillator directly to the output. 
            // If you hear this beep, the hardware is 100% working.
            try {
                const osc = rawCtx.createOscillator();
                const gain = rawCtx.createGain();
                osc.connect(gain);
                gain.connect(rawCtx.destination);
                osc.type = 'sine';
                osc.frequency.setValueAtTime(880, rawCtx.currentTime); // High pitch A5
                gain.gain.setValueAtTime(0.1, rawCtx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.001, rawCtx.currentTime + 0.1);
                osc.start(rawCtx.currentTime);
                osc.stop(rawCtx.currentTime + 0.1);
                this.reportStatus("Diagnostic Beep Scheduled (0.1s)");
            } catch (e) {
                this.reportStatus(`Beep failed: ${e}`);
            }

            // Step 8: Restore playback if there was a song playing
            if (cachedIsPlaying && cachedSongId) {
                this.reportStatus(`Restoring playback: ${cachedSongId} in ${cachedMixMode} mode`);
                // We keep isRebooting = true here, but use isInternal = true to bypass the check
                await this.playSong(cachedSongId, false, cachedMixMode, true);
            }

            this.reportStatus(`Hard Reboot Complete. Final Context: ${Tone.context.state}`);
        } catch (e) {
            this.reportError(`Hard Reboot CRITICAL FAILURE: ${e}`);
            console.error("Audio Reboot Error Detail:", e);
        } finally {
            this.isRebooting = false;
        }
    }

    public toggleMute(isMuted: boolean) {
        Tone.Destination.mute = isMuted;
        if (this.ctx?.masterGain) {
            // Smoothly ramp the gain to avoid clicks
            this.ctx.masterGain.gain.rampTo(isMuted ? 0 : 1, 0.1);
        }
    }

    private reportError(msg: string) {
        console.error(msg);
        this.logCounter++;
        if (this.onError) this.onError(msg);
    }

    private reportStatus(msg: string) {
        console.log(msg);
        this.logCounter++;
        if (this.onStatus) this.onStatus(msg);
    }

    private sanitizeDuration(dur: string | number | undefined): string | number {
        if (typeof dur === 'number') {
            return Math.min(4, Math.max(0.01, dur));
        }
        if (!dur || dur === "" || dur === "0" || dur === "0n") {
            return "16n";
        }
        return dur;
    }

    public getContextState() {
        const level = this.ctx?.meter ? this.ctx.meter.getValue() : -100;
        const song = this.currentSongId ? SONG_REGISTRY[this.currentSongId] : null;

        return {
            context: Tone.context.state,
            transport: Tone.Transport.state,
            bpm: Math.round(Tone.Transport.bpm.value),
            ppq: Tone.Transport.PPQ,
            initialized: this.isInitialized,
            level: Array.isArray(level) ? level[0] : (level as number),
            lastNote: this.lastNoteTriggered,
            activeMp3: this.backingPlayer ? (song?.mp3FileName || "Active") : null
        };
    }

    public async forceResume() {
        if (this.isRebooting) {
            this.reportStatus("Bypassing resume: Reboot in progress...");
            return;
        }
        try {
            const currentState = Tone.context.state;
            this.reportStatus(`Force resume triggered. Current state: ${currentState}`);

            if (currentState === 'closed') {
                this.reportStatus("Context is CLOSED. Initiating hard reboot...");
                await this.hardReboot();
                this.reportStatus(`Post-reboot context state: ${Tone.context.state}`);
                return;
            }

            if (currentState === 'suspended') {
                this.reportStatus("Context is SUSPENDED. Attempting resume...");
            }

            // Try start + resume - must happen within user gesture
            await Tone.start();
            await Tone.context.resume();

            // Verify context is running
            const finalState = Tone.context.state;
            if (finalState === 'running') {
                this.reportStatus(`Audio context resumed. State: ${finalState}`);

                // Also restart Transport if it's stopped and we were playing
                if (Tone.Transport.state !== 'started' && this.isPlaying) {
                    Tone.Transport.start("+0.1");
                    this.reportStatus("Transport restarted.");
                }

                // DEBUG: Transport Heartbeat
                // Check if time is actually moving
                const heartbeat = setInterval(() => {
                    if (Tone.Transport.state === 'started') {
                        console.log(`[Audio Heartbeat] Transport Time: ${Tone.Transport.seconds.toFixed(2)}s`);
                    } else {
                        console.log(`[Audio Heartbeat] Transport STOPPED`);
                        clearInterval(heartbeat);
                    }
                }, 1000);
                setTimeout(() => clearInterval(heartbeat), 10000); // Stop after 10s

            } else {
                this.reportStatus(`Resume attempted but state is: ${finalState}. Try Hard Reboot.`);
            }
        } catch (e) {
            this.reportError(`Force resume failed: ${e}. Try Hard Reboot.`);
        }
    }

    public async playSong(songId: string, loop: boolean = true, mixMode: MusicMixMode = 'MP3', isInternal: boolean = false) {
        const song = SONG_REGISTRY[songId];
        if (!song) return;

        const sessionId = ++this.loadSessionId;
        this.currentSongId = songId;
        this.currentMixMode = mixMode;
        this.isPlaying = true;

        try {
            // 1. COMPREHENSIVE RESET before starting any new operation
            // MUST happen synchronously at the start of the function call
            this.stopMusic();

            if (!this.isInitialized || !this.ctx || (Tone.context.state as string) === 'closed') {
                // Wait if anything is currently building the engine
                if (!isInternal && (this.isRebooting || this.isInitializing)) {
                    this.reportStatus("Waiting for audio engine to stabilize...");
                    let attempts = 0;
                    while ((this.isRebooting || this.isInitializing) && attempts < 50) {
                        await new Promise(r => setTimeout(r, 100));
                        attempts++;
                    }
                    if (this.loadSessionId !== sessionId) return;

                    // RE-CHECK after waiting
                    if ((!this.isInitialized || !this.ctx || (Tone.context.state as string) === 'closed')) {
                        this.reportStatus("Wait finished but engine not ready. Forcing init...");
                        await this.initialize();
                    }
                } else {
                    await this.initialize();
                }
            }
            if (this.loadSessionId !== sessionId) return;

            if (!this.ctx) {
                // Last ditch attempt
                this.reportStatus("Engine ctx missing. Retrying init...");
                await this.initialize();
                if (this.loadSessionId !== sessionId) return;
                if (!this.ctx) {
                    this.reportError("Could not start song: Audio engine not ready.");
                    return;
                }
            }

            this.reportStatus(`Resetting Engine & Loading: ${song.title} (${mixMode}) (Session: ${sessionId})`);

            await Tone.start();
            if (this.loadSessionId !== sessionId) return;

            // Only resume if suspended. If closed, initialize() should have caught it.
            if (Tone.context.state === 'suspended') {
                await Tone.context.resume();
            }
            if (this.loadSessionId !== sessionId) return;

            const now = Tone.now();

            // Clear filters
            this.ctx.masterFilter.frequency.setValueAtTime(20000, now);

            // Set Transport
            const configBpm = song.musicalData?.bpm || 120;
            Tone.Transport.bpm.setValueAtTime(configBpm, now);

            const totalBeats = song.structure.reduce((acc, s) => acc + s.duration, 0);
            const transportLoopEnd = `${Math.floor(totalBeats / 4)}:${totalBeats % 4}:0`;

            Tone.Transport.loop = loop;
            Tone.Transport.loopStart = 0;
            Tone.Transport.loopEnd = transportLoopEnd;

            let actualDuration = 0;
            let mp3Loaded = false;

            // MP3 LAYER - STRICT EXCLUSIVITY
            if (song.mp3FileName && mixMode === 'MP3') {
                try {
                    const mp3Url = `/mp3/${song.mp3FileName}`;
                    await new Promise<void>((resolve) => {
                        const player = new Tone.Player({
                            url: mp3Url,
                            context: this.ctx ? this.ctx.width.context : Tone.context,
                            onload: () => {
                                // GHOST PREVENTION: Check if we are still the relevant session
                                if (this.loadSessionId !== sessionId) {
                                    player.dispose();
                                    resolve();
                                    return;
                                }
                                this.pendingPlayer = null;
                                this.backingPlayer = player;
                                if (this.ctx) player.connect(this.ctx.width);
                                else player.toDestination();
                                player.sync().start(0);
                                actualDuration = player.buffer.duration;
                                this.reportStatus(`MP3 Layer Ready`);
                                resolve();
                            },
                            onerror: (e) => {
                                if (this.pendingPlayer === player) this.pendingPlayer = null;
                                console.warn(`Failed to load MP3 ${song.mp3FileName}`, e);
                                resolve();
                            }
                        });
                        // Track it immediately so stopMusic() can kill it if a new request comes in
                        this.pendingPlayer = player;
                    });
                } catch (e) {
                    console.warn("Error initializing MP3 player", e);
                }
            }

            // MIDI / SEQUENCE LAYER - STRICT EXCLUSIVITY
            else if (mixMode === 'SEQUENCE') {
                if (song.sequencedTracks?.length) {
                    const sequenceBeats = this.loadEmbeddedSequence(song.sequencedTracks, transportLoopEnd, loop);
                    if (sequenceBeats > 0) {
                        actualDuration = (sequenceBeats / configBpm) * 60;
                    }
                    this.reportStatus(`Embedded Sequence Ready (Duration: ${actualDuration.toFixed(2)}s)`);
                }
            }
            else if (mixMode === 'MIDI') {
                try {
                    if (song.midiFileName) {
                        const midiResult = await this.tryLoadMidi(song, transportLoopEnd, loop);
                        if (this.loadSessionId !== sessionId) return;
                        if (midiResult) {
                            actualDuration = midiResult.duration;
                            this.reportStatus(`MIDI File Ready`);
                        }
                    }
                } catch (e) {
                    this.reportError(`MIDI Parsing Error: ${e}`);
                }
            }

            if (this.loadSessionId !== sessionId) return;

            this.scheduleGameEvents(song);

            if (!loop) {
                this.scheduleEndDetection(song, actualDuration);
            }

            // Delay start slightly to ensure all parts are ready and context is settled (increased for stability)
            setTimeout(() => {
                if (Tone.Transport.state !== 'started' && this.loadSessionId === sessionId) {
                    Tone.Transport.start("+0.2");
                    this.reportStatus(`Transport Started`);
                }
            }, 500);
        } catch (e) {
            this.reportError(`playSong failure: ${e}`);
        }
    }

    private scheduleEndDetection(song: HuntrixSong, overrideDuration?: number) {
        if (this.endEventId !== null) Tone.Transport.clear(this.endEventId);

        const totalBeats = song.structure.reduce((acc, s) => acc + s.duration, 0);
        const configBpm = song.musicalData?.bpm || 120;
        const songDurationSec = overrideDuration || ((totalBeats / configBpm) * 60);

        this.endEventId = Tone.Transport.scheduleOnce(() => {
            if (this.onSongEnd) this.onSongEnd();
        }, songDurationSec + 2);
    }

    private getMidiInstrument(trackName: string, instrumentName: string, instrumentFamily: string, programNum: number, trackIndex: number): any {
        if (!this.ctx) return null;
        const name = (trackName + " " + instrumentName + " " + instrumentFamily).toLowerCase();
        if (programNum >= 0) {
            if (programNum <= 15) return this.ctx.midiPiano;
            if (programNum <= 31) return this.ctx.midiLead;
            if (programNum <= 39) return this.ctx.midiBass;
            if (programNum <= 55) return this.ctx.midiStrings;
            if (programNum <= 63) return this.ctx.midiBrass;
            if (programNum <= 71) return this.ctx.midiLead;
            if (programNum <= 79) return this.ctx.midiWind;
            if (programNum <= 95) return this.ctx.midiPads;
            if (programNum <= 111) return this.ctx.midiWind;
            if (programNum <= 127) return this.ctx.midiPads;
        }
        if (name.includes('bass')) return this.ctx.midiBass;
        if (name.includes('string') || name.includes('cello') || name.includes('violin')) return this.ctx.midiStrings;
        if (name.includes('piano') || name.includes('keys')) return this.ctx.midiPiano;
        if (name.includes('brass') || name.includes('horn')) return this.ctx.midiBrass;
        if (name.includes('flute') || name.includes('wind')) return this.ctx.midiWind;
        if (name.includes('guitar') || name.includes('lead')) return this.ctx.midiLead;
        if (name.includes('pad')) return this.ctx.midiPads;
        return this.ctx.midiPiano;
    }

    private loadEmbeddedSequence(tracks: SequencedTrack[], loopEnd: string, loop: boolean): number {
        if (!this.ctx) return 0;
        let maxDuration = 0;

        // Calculate total notes across all tracks for global throttling
        const totalNotesCount = tracks.reduce((sum, t) => sum + (t.notes ? t.notes.length : 0), 0);
        const GLOBAL_MAX = 2000;
        const globalStep = totalNotesCount > GLOBAL_MAX ? Math.ceil(totalNotesCount / GLOBAL_MAX) : 1;

        if (globalStep > 1) {
            this.reportStatus(`Global thinning: ${globalStep}x (Total items: ${totalNotesCount})`);
        }

        // Group tracks by instrument to handle shared polyphony limits correctly
        const tracksByInstrument = new Map<any, { notes: any[], trackNames: string[], isDrum: boolean }>();

        tracks.forEach((track, i) => {
            // Calculate track duration safely
            let trackMaxBeats = 0;
            track.notes.forEach(note => {
                const timeStr = String(note.time);
                const parts = timeStr.split(':').map(Number);
                const startBeats = (parts[0] * 4) + parts[1] + (parts[2] / 4);
                if (startBeats > trackMaxBeats) trackMaxBeats = startBeats;
            });
            if (trackMaxBeats > maxDuration) maxDuration = trackMaxBeats;

            const isDrum = track.name.toLowerCase().includes('drum') || track.name.toLowerCase().includes('percussion');
            const instrument = isDrum ? 'DRUMS' : this.getMidiInstrument(track.name, "", "", -1, i);

            if (!instrument) return;

            if (!tracksByInstrument.has(instrument)) {
                tracksByInstrument.set(instrument, { notes: [], trackNames: [], isDrum });
            }
            const entry = tracksByInstrument.get(instrument)!;
            entry.notes.push(...track.notes.map(n => ({ ...n, trackName: track.name })));
            entry.trackNames.push(track.name);
        });

        tracksByInstrument.forEach((entry, instrument) => {
            if (entry.isDrum) {
                // DRUM HANDLING
                // We'll use a safe limit of 8 simultaneous drum hits across all drum tracks
                let processedNotes = this.throttleNotes(entry.notes, 8);
                if (globalStep > 1) {
                    processedNotes = processedNotes.filter((_, idx) => idx % globalStep === 0);
                }
                const drumEvents = processedNotes.map(n => ({ midi: (n as any).midi || 0, name: n.name, time: n.time, velocity: n.velocity }));

                const drumPart = new Tone.Part((time, event: any) => {
                    try {
                        const v = event.velocity;
                        const n = event.name;

                        // Heuristic mapping if 'midi' property is missing
                        if (n.startsWith("C1") || n.startsWith("B0")) this.ctx?.kick.triggerAttackRelease("C1", "16n", time, v);
                        else if (n.startsWith("D1") || n.startsWith("E1")) this.ctx?.snare.triggerAttackRelease("16n", time, v);
                        else if (n.startsWith("F") && n.includes("1")) this.ctx?.hihat.triggerAttackRelease("32n", time, v * 0.5); // Closed HH
                        else if (n.startsWith("A") && n.includes("1")) this.ctx?.hihat.triggerAttackRelease("8n", time, v * 0.6); // Open HH
                        else if (n.includes("Crash") || n.startsWith("C#2")) this.ctx?.crash.triggerAttackRelease("16n", time, v * 0.8);
                        else if (n.includes("Clap") || n.startsWith("D#1")) this.ctx?.clap.triggerAttackRelease("16n", time, v);
                        else {
                            if (event.midi) {
                                switch (event.midi) {
                                    case 35: case 36: this.ctx?.kick.triggerAttackRelease("C1", "16n", time, v); break;
                                    case 38: case 40: this.ctx?.snare.triggerAttackRelease("16n", time, v); break;
                                    case 42: case 44: this.ctx?.hihat.triggerAttackRelease("32n", time, v * 0.5); break;
                                    case 46: this.ctx?.hihat.triggerAttackRelease("8n", time, v * 0.6); break;
                                    case 37: case 39: this.ctx?.clap.triggerAttackRelease("16n", time, v); break;
                                    case 49: case 57: this.ctx?.crash.triggerAttackRelease("16n", time, v * 0.8); break;
                                    default: this.ctx?.hihat.triggerAttackRelease("32n", time, v * 0.1);
                                }
                            } else {
                                this.ctx?.hihat.triggerAttackRelease("32n", time, v * 0.1);
                            }
                        }
                    } catch (e) { }
                }, drumEvents);
                (drumPart as any).context = (this.ctx as any).masterGain.context;
                drumPart.loop = loop;
                drumPart.loopEnd = loopEnd;
                drumPart.start(0);
                this.midiParts.push(drumPart);
            } else {
                // STANDARD MELODIC INSTRUMENT
                // STABILITY FIX: Detect Instrument Polyphony Limit
                const voiceLimit = (instrument as any).maxPolyphony || 6;
                // Reserve 1 voice for safety/release tails
                const safeLimit = Math.max(1, voiceLimit - 1);

                let processedNotes = this.throttleNotes(entry.notes, safeLimit);
                if (globalStep > 1) {
                    processedNotes = processedNotes.filter((_, idx) => idx % globalStep === 0);
                }

                const trackNameLabel = entry.trackNames.join('+').substring(0, 15);
                const part = new Tone.Part((time, note) => {
                    try {
                        this.lastNoteTriggered = `${trackNameLabel}: ${note.name}`;
                        const validDuration = this.sanitizeDuration(note.duration);
                        instrument.triggerAttackRelease(note.name, validDuration, time, note.velocity || 0.7);
                    } catch (e) { }
                }, processedNotes);
                (part as any).context = (this.ctx as any).masterGain.context;
                part.loop = loop;
                part.loopEnd = loopEnd;
                part.start(0);
                this.midiParts.push(part);
            }
        });

        return maxDuration + 2;
    }

    private async tryLoadMidi(song: HuntrixSong, loopEnd: string, loop: boolean): Promise<{ duration: number } | null> {
        if (!this.ctx) return null;
        try {
            const url = `/midi/${song.midiFileName}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP Error ${response.status}`);
            const arrayBuffer = await response.arrayBuffer();
            const midi = new Midi(arrayBuffer);

            if (midi.header.ppq) Tone.Transport.PPQ = midi.header.ppq;

            const totalMidiNotes = midi.tracks.reduce((sum, t) => sum + t.notes.length, 0);
            const midiGlobalMax = 2000;
            const midiGlobalStep = totalMidiNotes > midiGlobalMax ? Math.ceil(totalMidiNotes / midiGlobalMax) : 1;

            // Group tracks by instrument to handle shared polyphony limits correctly
            const midiTracksByInstrument = new Map<any, { notes: any[], trackNames: string[], isPercussion: boolean }>();

            midi.tracks.forEach((track, i) => {
                if (track.notes.length === 0) return;

                const isPercussion = (track.instrument && track.instrument.percussion) ||
                    (track.channel === 9) ||
                    (track.name && track.name.toLowerCase().includes('drum'));

                const instrument = isPercussion ? 'DRUMS' : this.getMidiInstrument(
                    track.name,
                    track.instrument ? track.instrument.name : "",
                    track.instrument ? track.instrument.family : "",
                    track.instrument ? track.instrument.number : -1,
                    i
                );

                if (!instrument) return;

                if (!midiTracksByInstrument.has(instrument)) {
                    midiTracksByInstrument.set(instrument, { notes: [], trackNames: [], isPercussion });
                }
                const entry = midiTracksByInstrument.get(instrument)!;
                entry.notes.push(...track.notes.map(n => ({ ...n, trackName: track.name })));
                entry.trackNames.push(track.name || `Track ${i}`);
            });

            midiTracksByInstrument.forEach((entry, instrument) => {
                if (entry.isPercussion) {
                    // STABILITY FIX: Limit drum density across all tracks
                    let processedNotes = this.throttleNotes(entry.notes, 8);
                    if (midiGlobalStep > 1) {
                        processedNotes = processedNotes.filter((_, idx) => idx % midiGlobalStep === 0);
                    }
                    const drumEvents = processedNotes.map(n => ({ midi: n.midi, time: n.ticks + "i", velocity: n.velocity }));
                    const drumPart = new Tone.Part((time, event: any) => {
                        try {
                            const v = event.velocity;
                            switch (event.midi) {
                                case 35: case 36: this.ctx?.kick.triggerAttackRelease("C1", "16n", time, v); break;
                                case 38: case 40: this.ctx?.snare.triggerAttackRelease("16n", time, v); break;
                                case 42: case 44: this.ctx?.hihat.triggerAttackRelease("32n", time, v * 0.5); break;
                                case 46: this.ctx?.hihat.triggerAttackRelease("8n", time, v * 0.6); break;
                                case 37: case 39: this.ctx?.clap.triggerAttackRelease("16n", time, v); break;
                                case 49: case 57: this.ctx?.crash.triggerAttackRelease("16n", time, v * 0.8); break;
                                default: this.ctx?.hihat.triggerAttackRelease("32n", time, v * 0.1);
                            }
                        } catch (e) { }
                    }, drumEvents);
                    (drumPart as any).context = (this.ctx as any).masterGain.context;
                    drumPart.loop = loop;
                    drumPart.loopEnd = loopEnd;
                    drumPart.start(0);
                    this.midiParts.push(drumPart);
                } else {
                    // STABILITY FIX: Prune based on actual instrument capacity
                    const voiceLimit = (instrument as any).maxPolyphony || 6;
                    const safeLimit = Math.max(1, voiceLimit - 1);

                    let processedNotes = this.throttleNotes(entry.notes, safeLimit);
                    if (midiGlobalStep > 1) {
                        processedNotes = processedNotes.filter((_, idx) => idx % midiGlobalStep === 0);
                    }
                    const events = processedNotes.map(n => ({
                        name: n.name,
                        time: n.ticks + "i",
                        duration: n.durationTicks + "i",
                        velocity: n.velocity
                    }));

                    const trackNameLabel = entry.trackNames.join('+').substring(0, 15);
                    const part = new Tone.Part((time, note: any) => {
                        try {
                            this.lastNoteTriggered = `${trackNameLabel}: ${note.name}`;
                            const validDuration = this.sanitizeDuration(note.duration);
                            instrument.triggerAttackRelease(note.name, validDuration, time, note.velocity);
                        } catch (e) { }
                    }, events);
                    (part as any).context = (this.ctx as any).masterGain.context;
                    part.loop = loop;
                    part.loopEnd = loopEnd;
                    part.start(0);
                    this.midiParts.push(part);
                }
            });
            return { duration: midi.duration };
        } catch (e) {
            console.warn("MIDI Load Failed:", e);
            return null;
        }
    }

    public stopMusic() {
        const now = Tone.now();

        // 1. Stop and Atomic Cancel the entire Transport timeline
        Tone.Transport.stop(now);
        // CRITICAL: Cancel from 0, not now, to ensure the entire timeline is wiped including ghost events
        Tone.Transport.cancel(0);

        // 2. Cleanup internal trackers
        if (this.endEventId !== null) {
            Tone.Transport.clear(this.endEventId);
            this.endEventId = null;
        }

        this.scheduledEvents.forEach(id => {
            try { Tone.Transport.clear(id); } catch (e) { }
        });
        this.scheduledEvents = [];

        // 3. Dispose all MIDI Parts
        this.midiParts.forEach(p => {
            try {
                p.stop(now);
                p.dispose();
            } catch (e) { }
        });
        this.midiParts = [];

        // 4. Dispose Backing Player AND Pending Player
        if (this.backingPlayer) {
            try {
                this.backingPlayer.stop(now);
                this.backingPlayer.dispose();
            } catch (e) { }
            this.backingPlayer = null;
        }
        if (this.pendingPlayer) {
            try {
                this.pendingPlayer.dispose();
            } catch (e) { }
            this.pendingPlayer = null;
        }

        // 5. Explicitly Release All Synth voices to kill hanging MIDI notes
        // AND reset voice tracking to prevent accumulation
        if (this.ctx) {
            try {
                const nodes = Object.values(this.ctx);
                nodes.forEach((node: any) => {
                    // Release all active voices
                    if (node && typeof node.releaseAll === 'function') {
                        try { node.releaseAll(now); } catch (e) { }
                    }
                    // Cancel any scheduled parameter changes
                    if (node && typeof node.cancelScheduledValues === 'function') {
                        try { node.cancelScheduledValues(now); } catch (e) { }
                    }
                    // CRITICAL FIX: Force-clear Tone.js internal voice tracking
                    // PolySynth maintains a _voices Map that can accumulate over time
                    if (node && node._voices && typeof node._voices.clear === 'function') {
                        try { node._voices.clear(); } catch (e) { }
                    }
                    // Also clear any _activeVoices array (some Tone.js versions use this)
                    if (node && Array.isArray(node._activeVoices)) {
                        try { node._activeVoices.length = 0; } catch (e) { }
                    }
                });
            } catch (e) { }
        }

        this.isPlaying = false;
        this.currentSongId = null;
        this.reportStatus("Audio Cleared & Reset.");
    }

    public pauseMusic() {
        const now = Tone.now();
        Tone.Transport.pause(now);
        this.isPlaying = false;
        if (this.ctx) {
            const polySynths = [
                this.ctx.midiLead, this.ctx.midiPiano, this.ctx.midiBass,
                this.ctx.midiPads, this.ctx.midiStrings, this.ctx.midiBrass,
                this.ctx.midiWind, this.ctx.uiSynth
            ];
            polySynths.forEach(s => s.releaseAll(now));
        }
    }

    public resumeMusic() {
        Tone.Transport.start(Tone.now());
        this.isPlaying = true;
    }

    public triggerDrop() {
        if (!this.ctx) return;
        const now = Tone.now();
        try {
            const freq = this.ctx.masterFilter.frequency;
            freq.cancelScheduledValues(now);
            freq.setValueAtTime(freq.value, now);
            freq.rampTo(20000, 0.05, now + 0.01);
            freq.rampTo(100, 0.5, now + 0.1);
            freq.rampTo(20000, 0.8, now + 1.0);
        } catch (e) { }
    }

    public setIntensity(state: string) {
        if (!this.ctx || Tone.context.state !== 'running') return;
        const now = Tone.now();
        switch (state) {
            case 'MENU': safeRamp(this.ctx.masterFilter.frequency, 800, 1, now); break;
            case 'CALM': safeRamp(this.ctx.masterFilter.frequency, 2000, 1, now); break;
            case 'COMBAT': case 'BOSS': safeRamp(this.ctx.masterFilter.frequency, 20000, 0.5, now); break;
        }
    }

    private scheduleGameEvents(song: HuntrixSong) {
        let timelineCursorBeats = 0;
        this.scheduledEvents = [];

        song.structure.forEach((section, sIdx) => {
            const startTimeStr = `${Math.floor(timelineCursorBeats / 4)}:${timelineCursorBeats % 4}:0`;
            const id = Tone.Transport.schedule((time) => {
                Tone.Draw.schedule(() => {
                    if (this.onSectionChange) this.onSectionChange!(section.type);
                }, time);
            }, startTimeStr);
            this.scheduledEvents.push(id);

            if (section.lyrics?.length) {
                const defaultStep = section.duration / section.lyrics.length;
                section.lyrics.forEach((line, idx) => {
                    const beatOffset = (section.lyricBeats && section.lyricBeats[idx] !== undefined) ? section.lyricBeats[idx] : (idx * defaultStep);
                    const absoluteBeat = timelineCursorBeats + beatOffset;

                    const bar = Math.floor(absoluteBeat / 4);
                    const beat = Math.floor(absoluteBeat % 4);
                    const sixteenth = (absoluteBeat % 1) * 4;
                    const lyricTime = `${bar}:${beat}:${sixteenth.toFixed(2)}`;

                    let prevText = section.lyrics[idx - 1] || "";
                    let nextText = section.lyrics[idx + 1] || "";

                    const lid = Tone.Transport.schedule((time) => {
                        Tone.Draw.schedule(() => {
                            if (this.onLyricUpdate) this.onLyricUpdate!({
                                prev: { text: prevText, singer: section.singer },
                                current: { text: line, singer: section.singer },
                                next: { text: nextText, singer: section.singer }
                            });
                        }, time);
                    }, lyricTime);
                    this.scheduledEvents.push(lid);
                });
            }
            timelineCursorBeats += section.duration;
        });
    }
    private pruneDenseEvents(notes: any[], maxPolyphony: number = 6): any[] {
        if (!notes || notes.length === 0) return [];

        // 1. Sort by time to enable linear scanning
        // Tone.js events usually have 'time' as string ("0:0:0") or number.
        // We need a comparable numeric time for the algorithm.
        const parseTime = (t: string | number): number => {
            if (typeof t === 'number') return t;
            if (typeof t === 'string') {
                const parts = t.split(':').map(Number);
                // bar * 16 + beat * 4 + sixteenth (approximate 16th note linear time)
                // This doesn't need to be perfect seconds, just monotonic for sorting
                return (parts[0] * 16) + (parts[1] * 4) + (parts[2] || 0);
            }
            return 0;
        };

        const sorted = [...notes].sort((a, b) => parseTime(a.time) - parseTime(b.time));
        const accepted: any[] = [];
        const activeVoices: { releaseTime: number, velocity: number, note: any }[] = [];

        for (const note of sorted) {
            const startTime = parseTime(note.time);

            // Calculate Release Time (rough estimate)
            // If duration is missing, assume 16n (0.25 beats -> 1 unit in our 16th-based time)
            let duration = 1;
            if (note.duration) {
                if (typeof note.duration === 'string') {
                    // Parse "8n", "16n" roughly
                    if (note.duration.includes('16n')) duration = 1;
                    else if (note.duration.includes('8n')) duration = 2;
                    else if (note.duration.includes('4n')) duration = 4;
                    else if (note.duration.includes('2n')) duration = 8;
                    else if (note.duration.includes('1n')) duration = 16;
                    else duration = 1; // Fallback
                } else if (typeof note.duration === 'number') {
                    // MIDI durations usually ticks. This might be tricky if mix of formats.
                    // Assuming standard MIDI ticks relative to PPQ, but here we normalized times?
                    // Let's assume input is standard Tone.Part note objects.
                    duration = 1; // Fail safe short duration
                }
            }
            const releaseTime = startTime + duration;

            // 1. Cleanup retired voices
            // Remove voices that have finished playing by this start time
            // Use a small buffer (0.1) to allow slight overlap for legato
            for (let i = activeVoices.length - 1; i >= 0; i--) {
                if (activeVoices[i].releaseTime <= startTime + 0.1) {
                    activeVoices.splice(i, 1);
                }
            }

            // 2. Check Polyphony Limit
            if (activeVoices.length < maxPolyphony) {
                // Space available
                activeVoices.push({ releaseTime, velocity: note.velocity || 0.7, note });
                accepted.push(note);
            } else {
                // Room full: Compete for space
                // Find the quietest active voice
                let quietestIdx = -1;
                let minVel = 999;

                for (let i = 0; i < activeVoices.length; i++) {
                    if (activeVoices[i].velocity < minVel) {
                        minVel = activeVoices[i].velocity;
                        quietestIdx = i;
                    }
                }

                const myVel = note.velocity || 0.7;

                // Aggressive pruning: Only steal checks if new note is SIGNIFICANTLY louder
                // or if the quietest note is very quiet.
                if (quietestIdx !== -1 && myVel > minVel + 0.1) {
                    // Steal voice!
                    // Is removing the old note from 'accepted' array possible? 
                    // No, it's already "accepted" and might be processed by Tone.
                    // But we are PRE-PROCESSING. So yes, we can remove it from 'accepted' list!
                    // BUT: 'accepted' is a linear list. Finding the exact note ref to remove is O(N).
                    // Optimization: We won't remove valid past notes, we just won't play THIS note.
                    // UNLESS we want to cut off a running note? No, MIDI doesn't work that way easily here.
                    // Better Strategy: Just Drop THIS note if we can't fit it.
                    // Or Drop if it's low priority. 

                    // Revised Strategy: Just Drop if Full. Keep it simple and stable.
                    // Dropping "future" notes is better than cutting "current" notes artifacts.
                    // To improve quality, we only really drop if THIS note is weak.
                    if (myVel > 0.4) {
                        // It's a strong note, maybe we squeeze it in?
                        // No. Hard limit is safer for "freezing".
                        // Log a drop for debugging?
                        // console.debug("Pruned note", note);
                    }
                } else {
                    // Voice full and I'm not loud enough to matter. Bye.
                }

                // For now, strict First-Come-First-Served with Velocity thresholding
                // If we are full, we strictly reject, UNLESS the new note is High Priority (Kick/Snare/Lead)
                // But this function doesn't know instrument type easily.
            }
        }

        return accepted;
    }

    private throttleNotes(notes: any[], limit: number = 6): any[] {
        // 1. Initial Garbage Collection
        if (!notes || notes.length === 0) return [];

        // 2. De-dupe (Same time+pitch)
        const seen = new Set<string>();
        const unique = notes.filter(n => {
            const timeKey = typeof n.time === 'number' ? n.time.toFixed(2) : String(n.time);
            const id = `${timeKey}-${n.name || n.midi}`;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });

        // 3. Velocity Floor (Silence suppression)
        // Drop anything quieter than 20% velocity unless it's a super sparse track
        const startCount = unique.length;
        const loudEnough = unique.filter(n => (n.velocity || 1) > 0.2);

        // 4. Intelligent Polyphony Pruning
        // Max voices allows per track.
        // For harmony/pads we might want 4-5. For leads 2-3.
        // We'll use a conservative default of 6 active voices per track.
        return this.pruneDenseEvents(loudEnough, limit);
    }
}

export const MusicManager = new GameAudioManager();